import { Table, Text, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { KpiTicketQuelle, KpiTicketSla } from "@msc/shared";

import { prozent, stunden } from "./ticketDarstellung.js";

/** Was ein Klick in der Tabelle öffnet: eine Quelle, oder „alle“ (rechtzeitig und verletzt). */
export type ReaktionKlick = KpiTicketQuelle | "alle";

/** Eine klickbare Zahl mit eindeutigem zugänglichem Namen (Zeile + Spalte). */
export function ZahlKnopf({ wert, label, onClick }: { wert: number; label: string; onClick: () => void }) {
  if (wert === 0) return <Text span size="sm">0</Text>;
  return (
    <UnstyledButton aria-label={`${label} (${wert})`} onClick={onClick} c="var(--msc-navy-text)" fz="sm" td="underline">
      {wert}
    </UnstyledButton>
  );
}

/**
 * SR-06 „Erste Reaktion" je Kundenabteilung. Unter 5 Tickets ausgegraut:
 * Eine Quote ohne Grundmenge ist eine Scheinaussage (Spec). Soll und Ø stehen
 * nebeneinander, beide in Arbeitszeit des SLA-Kalenders.
 */
export function ReaktionTabelle({
  daten,
  onKlick,
}: {
  daten: KpiTicketSla;
  onKlick: (quelle: ReaktionKlick, abteilungId: string, abteilung: string) => void;
}) {
  const { t } = useTranslation();
  if (daten.abteilungen.length === 0) {
    return <Text size="sm" c="dimmed">{t("pages.kpi.tickets.reaktion.leer")}</Text>;
  }
  return (
    <Table.ScrollContainer minWidth={640}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colAbteilung")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colAnzahl")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colRechtzeitig")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colVerletzt")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colQuote")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colSoll")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.reaktion.colMittel")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {daten.abteilungen.map((a) => (
            <Table.Tr key={a.abteilungId} data-wenige={String(a.wenige)} style={a.wenige ? { opacity: 0.5 } : undefined} title={a.wenige ? t("pages.kpi.tickets.reaktion.wenige") : undefined}>
              <Table.Td>{a.abteilung}</Table.Td>
              <Table.Td>
                <ZahlKnopf wert={a.anzahl} label={t("pages.kpi.tickets.reaktion.knopfAnzahl", { abteilung: a.abteilung })} onClick={() => onKlick("alle", a.abteilungId, a.abteilung)} />
              </Table.Td>
              <Table.Td>
                <ZahlKnopf wert={a.eingehalten} label={t("pages.kpi.tickets.reaktion.knopfRechtzeitig", { abteilung: a.abteilung })} onClick={() => onKlick("sla_eingehalten", a.abteilungId, a.abteilung)} />
              </Table.Td>
              <Table.Td>
                <ZahlKnopf wert={a.verletzt} label={t("pages.kpi.tickets.reaktion.knopfVerletzt", { abteilung: a.abteilung })} onClick={() => onKlick("sla_verletzt", a.abteilungId, a.abteilung)} />
              </Table.Td>
              <Table.Td>{prozent(a.quote)}</Table.Td>
              <Table.Td>{a.soll ?? "–"}</Table.Td>
              <Table.Td>{stunden(a.mittelMs)}</Table.Td>
            </Table.Tr>
          ))}
          <Table.Tr style={{ borderTop: "2px solid var(--msc-linie-stark)" }}>
            <Table.Td fw={700}>{t("pages.kpi.tickets.summe")}</Table.Td>
            <Table.Td fw={700}>{daten.summe.anzahl}</Table.Td>
            <Table.Td fw={700}>{daten.summe.eingehalten}</Table.Td>
            <Table.Td fw={700}>{daten.summe.verletzt}</Table.Td>
            <Table.Td fw={700}>{prozent(daten.summe.quote)}</Table.Td>
            <Table.Td />
            <Table.Td />
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
